import React, { useContext, useState, useMemo } from "react";
import Ctx from "../../contexts/AppContext";
import { formatPropAddr } from "../../utils/propertyAddr";

/* ─── Constants shared with PropsListPage's single-property share ──────── */
const APP_BASE_URL = "https://www.prorentz.com";
const propLink = (propId) => `${APP_BASE_URL}/?property=${propId}`;
const LOGO_URL = "/logo192.png";

/* ─── Canvas helpers (load + watermark) ───────────────────────────────── */
const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = (e) => reject(e);
  img.src = url;
});

const watermarkPhoto = async (photoUrl, logoImg) => {
  const photo = await loadImage(photoUrl);
  const W = photo.naturalWidth || photo.width;
  const H = photo.naturalHeight || photo.height;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(photo, 0, 0);
  const size = Math.max(48, Math.min(W * 0.08, 95));
  const pad = size * 0.28;
  const x = W - size - pad;
  const y = H - size - pad;
  const bgPad = Math.round(size * 0.12);
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.22)";
  ctx.shadowBlur = 16; ctx.shadowOffsetY = 3;
  ctx.fillStyle = "rgba(255,255,255,0.94)";
  const rx = x - bgPad, ry = y - bgPad;
  const rw = size + bgPad * 2, rh = size + bgPad * 2;
  const r = Math.round(size * 0.18);
  if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(rx, ry, rw, rh, r); ctx.fill(); }
  else { ctx.fillRect(rx, ry, rw, rh); }
  ctx.restore();
  ctx.drawImage(logoImg, x, y, size, size);
  return await new Promise((resolve, reject) => {
    canvas.toBlob(b => b ? resolve(b) : reject(new Error("toBlob failed")), "image/jpeg", 0.92);
  });
};

/* ─── Share icon ──────────────────────────────────────────────────────── */
const ShareIcon = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const OPTION_DEFS = [
  { key: "address",   icon: "📍", label: "Address" },
  { key: "rooms",     icon: "🛏️", label: "Rooms" },
  { key: "rent",      icon: "💰", label: "Rent" },
  { key: "deposit",   icon: "🏦", label: "Deposit" },
  { key: "amenities", icon: "✅", label: "Amenities" },
  { key: "photos",    icon: "📷", label: "Cover photo (1st of each)" },
  { key: "contact",   icon: "📞", label: "Your contact number" },
];

/**
 * SharePropertiesModal — global share flow triggered from the bottom
 * bar's center Share button. Two steps:
 *   1. SELECT — pick one or more properties (checkbox grid).
 *   2. INCLUDE — one shared "Select What to Include" panel applies to
 *      every selected property; a single combined message lists them all.
 *
 * The House Rental logo is always attached (watermarked onto each cover
 * photo, or as a standalone brand image when no photos are shared).
 */
export default function SharePropertiesModal() {
  const { t, h, props, shareModalOpen, setShareModalOpen, flash } = useContext(Ctx);

  const [step, setStep] = useState("select");        // "select" | "include"
  const [selIds, setSelIds] = useState(new Set());   // chosen property ids
  const [opts, setOpts] = useState({ address: true, rooms: true, rent: true, deposit: true, amenities: true, photos: false, contact: false });
  const [contactNum, setContactNum] = useState("");
  const [sharing, setSharing] = useState(false);
  // When exactly ONE property is selected, the user can pick which of
  // its photos to share (indexes into that property's photos array).
  // For multiple properties we fall back to cover-only (1st of each).
  const [photoSel, setPhotoSel] = useState(new Set());

  const selectedProps = useMemo(
    () => props.filter(p => selIds.has(p.id)),
    [props, selIds]
  );

  // The single selected property (or null) — used for per-image picking.
  const singleProp = selectedProps.length === 1 ? selectedProps[0] : null;
  const singlePhotos = (singleProp && singleProp.photos) ? singleProp.photos.slice(0, 6) : [];

  const close = () => {
    if (sharing) return;
    setShareModalOpen(false);
    // reset for next open
    setStep("select"); setSelIds(new Set());
    setOpts({ address: true, rooms: true, rent: true, deposit: true, amenities: true, photos: false, contact: false });
    setPhotoSel(new Set());
  };

  if (!shareModalOpen) return null;

  const toggleProp = (id) => setSelIds(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleOpt = (key) => {
    setOpts(o => {
      const next = { ...o, [key]: !o[key] };
      // When turning photos ON for a single property, pre-select all of
      // its photos so the default behaviour (share them) is intuitive.
      if (key === "photos" && next.photos && singleProp) {
        setPhotoSel(new Set(singlePhotos.map((_, i) => i)));
      }
      return next;
    });
  };
  const togglePhotoIdx = (i) => setPhotoSel(s => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  /* ── Build the combined message for all selected properties ── */
  const buildText = () => {
    const blocks = selectedProps.map((p, i) => {
      const lines = [`*${i + 1}. ${opts.address ? formatPropAddr(p) : `Property ${i + 1}`}*`];
      if (opts.rooms)                       lines.push(`🛏️ ${p.rooms} ${t.rooms || "Rooms"}`);
      if (opts.rent)                        lines.push(`💰 Rent: ₹${p.rent.toLocaleString()}/mo`);
      if (opts.deposit)                     lines.push(`🏦 Deposit: ₹${p.dep.toLocaleString()}`);
      if (opts.amenities && p.amen?.length) lines.push(`✅ ${p.amen.join(", ")}`);
      lines.push(`👇 ${propLink(p.id)}`);
      return lines.join("\n");
    });
    const head = selectedProps.length > 1
      ? `🏠 *${selectedProps.length} Properties Available for Rent*`
      : `🏠 *Property Available for Rent*`;
    const tail = [];
    if (opts.contact && contactNum.trim()) tail.push(`📞 ${contactNum.trim()}`);
    tail.push("Interested? Contact me for details!");
    return [head, "", blocks.join("\n\n"), "", tail.join("\n")].join("\n");
  };

  /* ── Execute the share ── */
  const doShare = async () => {
    if (!selectedProps.length) { flash("Select at least one property", "error"); return; }
    setSharing(true);
    const text = buildText();

    let logoImg = null;
    try { logoImg = await loadImage(`${window.location.origin}${LOGO_URL}`); } catch (_) {}

    // Pick the photo URLs to attach (watermarked), capped at 6 for the
    // Web Share file limit:
    //   - Single property → exactly the photos the user ticked.
    //   - Multiple properties → cover photo (1st) of each.
    let files = [];
    if (opts.photos) {
      let urls = [];
      if (singleProp) {
        urls = singlePhotos.filter((_, i) => photoSel.has(i)).slice(0, 6);
        // If none ticked but photos exist, fall back to the cover so the
        // toggle never silently sends nothing.
        if (!urls.length && singlePhotos.length) urls = [singlePhotos[0]];
      } else {
        urls = selectedProps.map(p => (p.photos && p.photos[0]) || null).filter(Boolean).slice(0, 6);
      }
      if (urls.length) {
        const results = await Promise.allSettled(urls.map(async (url, i) => {
          let blob;
          try { blob = logoImg ? await watermarkPhoto(url, logoImg) : await (await fetch(url)).blob(); }
          catch (_) { blob = await (await fetch(url)).blob(); }
          return new File([blob], `property-${i + 1}.jpg`, { type: blob.type || "image/jpeg" });
        }));
        files = results.filter(r => r.status === "fulfilled").map(r => r.value);
      }
    }
    // No photos → attach the brand logo so the share is still branded.
    if (!files.length && logoImg) {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = logoImg.naturalWidth || 192;
        canvas.height = logoImg.naturalHeight || 192;
        canvas.getContext("2d").drawImage(logoImg, 0, 0);
        const blob = await new Promise((res, rej) => canvas.toBlob(b => b ? res(b) : rej(), "image/png"));
        files.push(new File([blob], "house-rental-logo.png", { type: "image/png" }));
      } catch (_) {}
    }

    const single = selectedProps.length === 1;
    const shareData = {
      title: single ? `Property – ${formatPropAddr(selectedProps[0])}` : `${selectedProps.length} Properties for Rent`,
      text,
    };
    // For a single property, a rich-preview URL is nice. For many, the
    // links live inside the text (one per property) so we omit url.
    if (single) shareData.url = propLink(selectedProps[0].id);
    if (files.length && navigator.canShare && navigator.canShare({ files })) {
      shareData.files = files;
    }

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(text);
        flash("Copied to clipboard — paste anywhere to share!");
      }
    } catch (_) { /* cancelled */ }

    setSharing(false);
    close();
  };

  /* ════════════════════════════════════════════════════════════════════ */
  const sheet = {
    background: h.cd, borderRadius: "20px 20px 0 0",
    width: "100%", maxWidth: 430, margin: "0 auto",
    padding: "16px 16px 32px", boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
    maxHeight: "90vh", overflowY: "auto",
  };

  return (
    <div onClick={close} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={sheet}>
        <div style={{ width: 36, height: 4, background: h.bd, borderRadius: 2, margin: "0 auto 16px" }} />

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <ShareIcon size={18} color={h.ac} />
          <span style={{ color: h.tx, fontSize: 16, fontWeight: 700 }}>
            {step === "select" ? "Share Properties" : "What to Include"}
          </span>
        </div>
        <div style={{ color: h.ts, fontSize: 12, marginBottom: 16, paddingLeft: 28 }}>
          {step === "select"
            ? `Select one or more properties to share${selIds.size ? ` · ${selIds.size} selected` : ""}`
            : `Applies to all ${selectedProps.length} selected propert${selectedProps.length === 1 ? "y" : "ies"}`}
        </div>

        {/* ── STEP 1: SELECT PROPERTIES ── */}
        {step === "select" && (
          <>
            {props.length === 0 && (
              <div style={{ color: h.ts, fontSize: 13, textAlign: "center", padding: 24 }}>
                No properties to share yet.
              </div>
            )}
            <div style={{
              display: "flex", flexDirection: "column", gap: 8, marginBottom: 16,
              // Cap the list height so a long portfolio scrolls WITHIN this
              // region instead of pushing the "Next" button off-screen.
              // overscroll-contain keeps the scroll from bubbling to the
              // sheet/page; the right padding leaves room for the scrollbar.
              maxHeight: "min(48vh, 380px)", overflowY: "auto",
              overscrollBehavior: "contain", WebkitOverflowScrolling: "touch",
              paddingRight: 4, marginRight: -4,
            }}>
              {props.map(p => {
                const on = selIds.has(p.id);
                const cover = p.photos && p.photos[0];
                return (
                  <div key={p.id} onClick={() => toggleProp(p.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: 8, borderRadius: 12,
                      background: on ? `${h.ac}0D` : h.sf,
                      border: `1px solid ${on ? h.ac + "35" : h.bd}`,
                      cursor: "pointer", transition: "all 0.15s",
                    }}>
                    {/* checkbox */}
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${on ? h.ac : h.b2}`, background: on ? h.ac : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {on && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,6 5,9 10,3" /></svg>}
                    </div>
                    {/* thumb */}
                    <div style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: `${h.ac}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {cover
                        ? <img src={cover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <span style={{ fontSize: 18 }}>🏠</span>}
                    </div>
                    {/* info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formatPropAddr(p)}</div>
                      <div style={{ color: h.ts, fontSize: 11 }}>{p.rooms} {t.rooms} · ₹{p.rent.toLocaleString()}/mo</div>
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: p.occ ? `${h.gn}18` : `${h.wn}18`, color: p.occ ? h.gn : h.wn, textTransform: "uppercase", flexShrink: 0 }}>
                      {p.occ ? t.occupied : t.vacant}
                    </span>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => setStep("include")}
              disabled={selIds.size === 0}
              style={{
                width: "100%", background: selIds.size ? h.ac : `${h.ac}55`,
                border: "none", color: "#fff", padding: "13px", borderRadius: 12,
                cursor: selIds.size ? "pointer" : "default", fontSize: 14, fontWeight: 600,
              }}>
              Next · {selIds.size} selected →
            </button>
          </>
        )}

        {/* ── STEP 2: SELECT WHAT TO INCLUDE ── */}
        {step === "include" && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
              {OPTION_DEFS.map(o => {
                const on = !!opts[o.key];
                return (
                  <label key={o.key} onClick={() => toggleOpt(o.key)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "11px 12px", borderRadius: 12,
                      background: on ? `${h.ac}0D` : h.sf,
                      border: `1px solid ${on ? h.ac + "35" : h.bd}`,
                      cursor: "pointer",
                    }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${on ? h.ac : h.b2}`, background: on ? h.ac : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {on && <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,6 5,9 10,3" /></svg>}
                    </div>
                    <span style={{ fontSize: 15 }}>{o.icon}</span>
                    <span style={{ color: h.tx, fontSize: 13, fontWeight: 500 }}>
                      {/* For a single property the photos row picks specific
                          images, so drop the "1st of each" wording. */}
                      {o.key === "photos"
                        ? (singleProp
                            ? (singlePhotos.length ? `Photos (${photoSel.size}/${singlePhotos.length} selected)` : "Photos (none uploaded)")
                            : "Cover photo (1st of each)")
                        : o.label}
                    </span>
                  </label>
                );
              })}
            </div>

            {/* Per-image picker — only when ONE property is selected and
                the photos option is on. Tap thumbnails to include/exclude. */}
            {opts.photos && singleProp && singlePhotos.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {singlePhotos.map((url, i) => {
                    const on = photoSel.has(i);
                    return (
                      <div key={i} onClick={() => togglePhotoIdx(i)}
                        style={{ position: "relative", width: 72, height: 72, borderRadius: 10, overflow: "hidden", cursor: "pointer", border: `2px solid ${on ? h.ac : h.bd}`, opacity: on ? 1 : 0.55, transition: "all 0.15s" }}>
                        <img src={url} alt={`View ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        {/* check badge */}
                        <div style={{ position: "absolute", top: 4, right: 4, width: 18, height: 18, borderRadius: 9, background: on ? h.ac : "rgba(255,255,255,0.85)", border: `1.5px solid #fff`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
                          {on && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,6 5,9 10,3" /></svg>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ color: h.tt || h.sc, fontSize: 10, marginTop: 6 }}>
                  Tap to choose which photos to share (up to 6). Each gets your logo watermark.
                </div>
              </div>
            )}

            {opts.contact && (
              <input
                type="tel" placeholder="Your contact number e.g. 9876543210"
                value={contactNum} onChange={e => setContactNum(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", background: h.ib, border: `1px solid ${h.b2}`, borderRadius: 8, padding: "10px 12px", color: h.tx, fontSize: 13, marginBottom: 14, outline: "none" }}
              />
            )}

            {/* Brand + attachments note */}
            <div style={{ color: h.ts, fontSize: 11, marginBottom: 14, padding: "10px 12px", borderRadius: 10, background: `${h.ac}08`, border: `1px solid ${h.ac}15` }}>
              🏠 ProRentz logo is attached to every share.
              {!opts.photos
                ? " Tick \"Photos\" to attach property images."
                : singleProp
                  ? ` ${Math.max(1, photoSel.size)} photo${Math.max(1, photoSel.size) > 1 ? "s" : ""} will be shared (with logo watermark).`
                  : " Each property's cover photo is included (with logo watermark)."}
            </div>

            {/* Live preview */}
            <div style={{ background: h.sf, borderRadius: 12, padding: "12px 14px", marginBottom: 16, border: `1px solid ${h.bd}` }}>
              <div style={{ color: h.ts, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>Preview</div>
              <pre style={{ color: h.tx, fontSize: 12, lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{buildText()}</pre>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep("select")} disabled={sharing}
                style={{ flex: 1, background: h.sf, border: `1px solid ${h.bd}`, color: h.ts, padding: "13px", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
                ← Back
              </button>
              <button onClick={doShare} disabled={sharing}
                style={{ flex: 2, background: sharing ? `${h.ac}99` : h.ac, border: "none", color: "#fff", padding: "13px", borderRadius: 12, cursor: sharing ? "default" : "pointer", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <ShareIcon size={15} color="#fff" />
                {sharing ? "Sharing…" : `Share ${selectedProps.length}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
